import React, { useState } from 'react';
import _ from 'underscore';
import JBrowseService from '../services/jbrowse.service';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Typography,
  Box,
  Grid,
  List,
  ListItem,
  Divider,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#4caf50' // Green
    },
    secondary: {
      main: '#9e9e9e' // Grey
    }
  }
});

const SampleSequenceModal = (props) => {
  const {
    sampleNames,
    sampleSequenceModalIsOpen,
    setSampleSequenceIsOpen, // Ensure this is either true or false, not undefined
    setLoading,
    selectedAssembly,
    selectedTracks,
    location,
    setAccessVariantIsOpen,
    currentUser
  } = props;

  const [sampleFilter, setSampleFilter] = useState('');
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [showSequences, setShowSequences] = useState(false);
  const [sequenceList, setSequenceList] = useState([]);
  const [spinner, setSpinner ] = useState(false);

  const charsPerLine = 60;

  const filteredSamples = sampleNames
    .filter((sample) => !selectedSamples.includes(sample))
    .filter((sample) => sample.toLowerCase().includes(sampleFilter.toLowerCase()));

  const handleGetSequences = () => {
    setSpinner(true);
    const vcfFiles = selectedTracks.filter((file) => file.endsWith('.vcf'));

    const obj = {
      loc: location,
      samples: selectedSamples.join(','),
      reference: selectedAssembly,
      vcf: vcfFiles[0],
      user: currentUser.email,
      company: currentUser.DirectoryName
    };

    JBrowseService.sampleSpecificSequence(obj).then(
      (response) => {
        const arr = response.result.split('\n');
        const dat = [];
        _.each(arr, (value) => {
          dat.push(value);
        });

        const inputArray = dat.slice(0, -1);
        const samples = [];
        for (let i = 0; i < inputArray.length; i += 2) {
          const loc = inputArray[i].replace('>', '');
          const sequence = inputArray[i + 1];
          samples.push({ loc, sequence });
        }

        setSequenceList(samples);
        setShowSequences(true);
        setSpinner(false);
      },
      () => {
        setSpinner(false);
      }
    );
  };

  const selectSample = (sample) => {
    setSelectedSamples((prev) => [...prev, sample]);
  };

  const removeSample = (sample) => {
    setSelectedSamples((prev) => prev.filter((s) => s !== sample));
  };

  const selectAll = () => {
    setSelectedSamples(filteredSamples);
  };

  const removeAll = () => {
    setSelectedSamples([]);
  };

  const closeModal = () => {
    setSampleSequenceIsOpen(false);
  };

  const onGoBack = () => {
    setSampleSequenceIsOpen(false);
    setTimeout(() => {
      setAccessVariantIsOpen(true);
    }, 100);
  };

  const copyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    alert('Sequence copied to clipboard!');
  };

  const downloadSequence = (seq) => {
    const filename = `${location}.fa`;
    downloadFile(seq, filename);
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Ensure 'open' is passed a boolean */}
      <Dialog
        open={Boolean(setSampleSequenceIsOpen)}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6">Get Sample Sequence</Typography>
          <IconButton onClick={closeModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
            
          {!showSequences && (
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Search Samples"
                variant="outlined"
                margin="normal"
                placeholder="Search..."
                value={sampleFilter}
                onChange={(e) => setSampleFilter(e.target.value)}
              />

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Available Samples
                  </Typography>
                  <Paper variant="outlined" sx={{ maxHeight: 250, overflow: 'auto' }}>
                    <List dense>
                      <ListItem
                        button
                        onClick={selectAll}
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '&:hover': { backgroundColor: 'primary.dark' }
                        }}
                      >
                        Select All
                      </ListItem>
                      {filteredSamples.map((sample, index) => (
                        <React.Fragment key={index}>
                          <ListItem
                            button
                            onClick={() => selectSample(sample)}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'grey.100'
                              }
                            }}
                          >
                            {sample}
                          </ListItem>
                          <Divider />
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Selected Samples
                  </Typography>
                  <Paper variant="outlined" sx={{ maxHeight: 250, overflow: 'auto' }}>
                    <List dense>
                      <ListItem
                        button
                        onClick={removeAll}
                        sx={{
                          backgroundColor: 'error.main',
                          color: 'white',
                          '&:hover': { backgroundColor: 'error.dark' }
                        }}
                      >
                        Remove All
                      </ListItem>
                      {selectedSamples.map((sample, index) => (
                        <React.Fragment key={index}>
                          <ListItem
                            button
                            onClick={() => removeSample(sample)}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'grey.100'
                              }
                            }}
                          >
                            {sample}
                          </ListItem>
                          <Divider />
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {showSequences && (
            <Box>
              {sequenceList.map((seqObj, seqIndex) => (
                <Box key={seqIndex} sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    {seqObj.loc}{' '}
                    <Typography variant="caption" component="span">
                      - {(seqObj.sequence.length * 2 / 1024).toFixed(2)} KB
                    </Typography>
                  </Typography>
                  <Box sx={{ mb: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => copyToClipboard(seqObj.sequence)}
                      sx={{ mr: 1 }}
                    >
                      Copy to Clipboard
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => downloadSequence(seqObj.sequence)}
                      sx={{ mr: 1 }}
                    >
                      Download Sequence
                    </Button>
                  </Box>
                  <Paper
                    variant="outlined"
                    sx={{ maxHeight: 400, overflow: 'auto', p: 1, backgroundColor: 'grey.50' }}
                  >
                    {seqObj.sequence
                      .match(new RegExp(`.{1,${charsPerLine}}`, 'g'))
                      .map((line, lineIndex) => (
                        <Box key={lineIndex} sx={{ display: 'flex' }}>
                          <Typography
                            variant="body2"
                            sx={{ width: 60, flexShrink: 0, color: 'grey.600' }}
                          >
                            {lineIndex * charsPerLine + 1}
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            {line}
                          </Typography>
                        </Box>
                      ))}
                  </Paper>
                </Box>
              ))}
            </Box>
          )}
            {spinner && (
                <div className="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
            )}
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<ArrowBackIcon />}
            onClick={onGoBack}
          >
            Go Back
          </Button>
          {!showSequences && (
            <Button variant="contained" color="primary" onClick={handleGetSequences}>
              Get Sequences
            </Button>
          )}
          <Button variant="contained" onClick={closeModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default React.memo(SampleSequenceModal);

function downloadFile(data, filename) {
  if (!data) {
    return;
  }
  if (!filename) {
    filename = 'download.json';
  }
  if (typeof data === 'object') {
    data = JSON.stringify(data, undefined, 2);
  }
  const blob = new Blob([data], { type: 'text/json' });
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename);
  } else {
    const e = document.createEvent('MouseEvents');
    const a = document.createElement('a');
    a.download = filename;
    a.href = window.URL.createObjectURL(blob);
    a.dataset.downloadurl = ['text/json', a.download, a.href].join(':');
    e.initEvent('click', true, false, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
    a.dispatchEvent(e);
  }
}
